import * as React from 'react'
import { Modal } from '../../Modal'
import { Button } from '../../Button'
import { Checkbox } from '../../Checkbox'
import { Text } from '../../Text'
import type { SavedView } from '../../../../app/lib/api/saved-views'
import {
  body as confirmModalBody,
  title as confirmModalTitle,
} from '../../ConfirmModal/ConfirmModal.css'
import { getSavedViewIcon } from './icons'
import * as s from './SavedViews.css'
import { useT } from '../../../../app/lib/i18n'

export interface SavedViewSettingsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sharedAvailable: SavedView[]
  includedSharedIds: string[]
  onChangeIncluded: (ids: string[]) => void
}

export function SavedViewSettingsModal({
  open,
  onOpenChange,
  sharedAvailable,
  includedSharedIds,
  onChangeIncluded,
}: SavedViewSettingsModalProps) {
  const t = useT()
  const [draft, setDraft] = React.useState<string[]>(includedSharedIds)

  React.useEffect(() => {
    if (open) setDraft(includedSharedIds)
  }, [open, includedSharedIds])

  const toggle = (id: string, checked: boolean) => {
    setDraft((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id]
      return prev.filter((x) => x !== id)
    })
  }

  const handleDone = () => {
    onChangeIncluded(draft)
    onOpenChange(false)
  }

  return (
    <Modal
      open={open}
      setOpen={onOpenChange}
      minWidth="520px"
      maxWidth="640px"
      minHeight="auto"
    >
      <div className={confirmModalBody}>
        <h3 className={confirmModalTitle}>{t('shared.savedView.settings')}</h3>
        <div className={s.form}>
          <div className={s.field}>
            <Text level="body-small" weight="medium" className={s.fieldLabel}>
              Shared Views
            </Text>
            <Text level="body-small" style={{ color: 'var(--color-fgMuted, #9CA3AF)' }}>
              Choose which organization-shared views appear in your dropdown.
            </Text>
          </div>

          {sharedAvailable.length === 0 ? (
            <div className={s.emptyHint}>
              No shared views available from your organizations yet.
            </div>
          ) : (
            <div className={s.settingsList}>
              {sharedAvailable.map((view) => {
                const Icon = getSavedViewIcon(view.icon)
                const checked = draft.includes(view.id)
                return (
                  <label key={view.id} className={s.settingsItem}>
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v: boolean) => toggle(view.id, v)}
                      aria-label={`Include ${view.name}`}
                    />
                    <span
                      className={s.optionIcon}
                      style={{ backgroundColor: `${view.color}22`, color: view.color }}
                    >
                      <Icon size={14} />
                    </span>
                    <span className={s.settingsMeta}>
                      <span className={s.settingsName}>{view.name}</span>
                      {view.ownerName ? (
                        <span className={s.settingsOwner}>
                          Shared by {view.ownerName}
                        </span>
                      ) : null}
                    </span>
                  </label>
                )
              })}
            </div>
          )}

          <div className={s.formFooterBar}>
            <div className={s.formFooterRight}>
              <Button
                color="neutral"
                variant="outline"
                size="md"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button color="primary" size="md" onClick={handleDone}>
                Done
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}
